import { IHeaderAngularComp } from '@ag-grid-community/angular';
import { IHeaderParams } from '@ag-grid-community/core';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import { IconComponent } from '@shared/components/icon/icon.component';
import { AgSetColumnsVisible } from '@shared/utils';

export interface TotalDistributionComponentParams extends IHeaderParams {
  afterToggle?: () => void;
  collapsedByDefault: boolean;
  columnsToCollapse: string[];
  ignoreIfInvisible: string[];
  localStorageKey: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, NgClass, ReactiveFormsModule],
  styles: [
    `
      :host {
        display: flex;
        justify-content: center;
        width: 100%;
        height: 100%;
      }
    `,
  ],
  template: `
    <div class="flex items-center justify-center space-x-1">
      <div [ngClass]="templateClasses" [innerHTML]="params.template"></div>

      @if (params.columnsToCollapse.length) {
        <aux-icon
          class="ml-1 shrink-0 cursor-pointer"
          (click)="onToggle()"
          [name]="visible ? 'ChevronLeft' : 'ChevronRight'"
        />
      }
    </div>
  `,
})
export class TotalDistributionHeaderComponent implements IHeaderAngularComp {
  readonly changeDetectorRef = inject(ChangeDetectorRef);

  params!: TotalDistributionComponentParams;
  templateClasses = ['text-aux-black', 'text-base', 'whitespace-normal', 'text-center'];
  visible = true;

  agInit(params: TotalDistributionComponentParams): void {
    this.params = params;
    setTimeout(() => {
      this.initChevron();
      AgSetColumnsVisible({
        gridApi: this.params.api,
        keys: this.params.columnsToCollapse,
        visible: this.visible,
      });
      params.api.sizeColumnsToFit();
      this.changeDetectorRef.detectChanges();
    });
  }

  private initChevron(): void {
    const checkIsDependentVisible = this.params.ignoreIfInvisible.every((key) =>
      this.params.api.getColumn(key)?.isVisible()
    );

    switch (localStorage.getItem(this.params.localStorageKey)) {
      case 'false':
        this.visible = false;

        return;
      case 'true':
        this.visible = checkIsDependentVisible;

        return;
      default:
        this.visible = !this.params.collapsedByDefault && checkIsDependentVisible;
    }
  }

  onToggle(): void {
    this.toggleColumnsVisibility();

    if (this.params.afterToggle) {
      this.params.afterToggle();
    }
  }

  refresh(): boolean {
    return false;
  }

  private toggleColumnsVisibility(): void {
    this.visible = !this.visible;
    AgSetColumnsVisible({
      gridApi: this.params.api,
      keys: this.params.columnsToCollapse,
      visible: this.visible,
    });

    if (this.params.localStorageKey) {
      localStorage.setItem(this.params.localStorageKey, this.visible.toString());
    }

    this.params.api.sizeColumnsToFit();
  }
}
